import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, 
  Users, 
  Database, 
  History, 
  Check, 
  Brain, 
  Shield, 
  Phone, 
  MapPin, 
  Zap,
  LayoutGrid,
  Smartphone,
  Mail,
  Clock,
  Bell,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Inbox,
  FileText,
  Video,
  Menu,
  Search,
  MoreHorizontal
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TwoWayMessagingPage = () => {
  // Animation state for the Unified History stream
  const [visibleItems, setVisibleItems] = useState([0, 1, 2]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleItems(prev => {
        const next = prev.length < 6 ? [...prev, prev.length] : [0, 1, 2];
        return prev.length < 6 ? next : [0, 1, 2]; // Reset loop for demo effect
      });
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const historyItems = [
    {
      type: 'sms',
      sender: 'Patient',
      name: 'Sarah Miller',
      content: "Hi, I'm running about 10 minutes late for my appointment.",
      time: '8:50 AM',
      icon: MessageSquare,
      color: 'bg-blue-100 text-blue-600',
      align: 'left'
    },
    {
      type: 'ai',
      sender: 'AI Agent',
      name: 'Amy (Receptionist)',
      content: "No problem, Sarah! Thanks for letting us know. We'll see you soon.",
      time: '8:50 AM',
      icon: Brain,
      color: 'bg-purple-100 text-purple-600',
      align: 'right'
    },
    {
      type: 'system',
      sender: 'System',
      name: 'PMS Update',
      content: "Insurance Verified: Delta Dental PPO (100% Prev, 80% Basic)",
      time: '9:05 AM',
      icon: Shield,
      color: 'bg-green-100 text-green-600',
      align: 'center'
    },
    {
      type: 'system',
      sender: 'System',
      name: 'Check-In',
      content: "Patient checked in at front desk kiosk",
      time: '9:15 AM',
      icon: MapPin,
      color: 'bg-orange-100 text-orange-600',
      align: 'center'
    },
    {
      type: 'voice',
      sender: 'Staff',
      name: 'Front Desk',
      content: "Outbound Call - 2m 14s (Post-op instructions discussed)",
      time: '2:30 PM',
      icon: Phone,
      color: 'bg-gray-100 text-gray-600',
      align: 'right'
    },
    {
      type: 'sms',
      sender: 'Staff',
      name: 'Dr. Smith',
      content: "Please send her the link to the post-op care video as well.",
      time: '2:35 PM',
      icon: Users,
      color: 'bg-yellow-100 text-yellow-600',
      isInternal: true,
      align: 'right'
    }
  ];

  const VideoIcon = ({ className }) => (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.05);
        }
        .app-shadow {
          box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.1), 0 10px 20px -5px rgba(0, 0, 0, 0.04);
        }
      `}</style>
      
      {/* Hero Section - Inspired by Spruce's clean, app-focused hero */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-slate-50">
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                <span>The All-in-One Patient Platform</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-[1.1] text-slate-900">
                The only two-way messaging platform <br/>
                <span className="text-blue-600">suitable for both AI and human staff.</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Phone, text, video, e-fax, and team chat—unified in one inbox and supercharged by dental-native AI that understands your practice.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
                    Start for Free
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  className="text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-8 py-6 rounded-xl text-lg font-medium transition-all"
                >
                  See how it works
                </Button>
              </div>
              
              <div className="mt-10 flex items-center gap-8 text-sm font-medium text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>HIPAA Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Seamless PMS Migration</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>14-Day Free Trial</span>
                </div>
              </div>
            </div>
            
            {/* Right: Modern App Interface Mockup */}
            <div className="relative">
              {/* Background Glows */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-100/50 rounded-full blur-3xl -z-10"></div>
              
              {/* Main App Container */}
              <div className="relative bg-white rounded-3xl app-shadow border border-slate-200 overflow-hidden transform rotate-1 hover:rotate-0 transition-all duration-500">
                {/* App Header */}
                <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="text-sm font-medium opacity-80">DentalAI Unified Inbox</div>
                  <Menu className="w-5 h-5 opacity-80" />
                </div>
                
                {/* App Layout */}
                <div className="flex h-[450px]">
                   {/* Sidebar */}
                   <div className="w-16 bg-slate-50 border-r border-slate-200 flex flex-col items-center py-6 gap-6">
                      <div className="p-2 bg-blue-100 rounded-xl text-blue-600"><Inbox className="w-5 h-5" /></div>
                      <div className="p-2 text-slate-400 hover:text-slate-600"><Phone className="w-5 h-5" /></div>
                      <div className="p-2 text-slate-400 hover:text-slate-600"><Users className="w-5 h-5" /></div>
                      <div className="p-2 text-slate-400 hover:text-slate-600"><Video className="w-5 h-5" /></div>
                   </div>

                   {/* Message List */}
                   <div className="w-64 bg-white border-r border-slate-100 flex flex-col">
                      <div className="p-4 border-b border-slate-100">
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-slate-50 rounded-lg text-sm outline-none" />
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <div className="p-4 bg-blue-50 border-l-4 border-blue-600 cursor-pointer">
                           <div className="flex justify-between mb-1">
                             <span className="font-bold text-sm text-slate-900">Sarah Miller</span>
                             <span className="text-[10px] text-slate-500">10:42 AM</span>
                           </div>
                           <p className="text-xs text-slate-600 truncate">I'll be there in 10 mins!</p>
                        </div>
                        <div className="p-4 hover:bg-slate-50 cursor-pointer border-l-4 border-transparent">
                           <div className="flex justify-between mb-1">
                             <span className="font-bold text-sm text-slate-900">Mike Ross</span>
                             <span className="text-[10px] text-slate-500">9:15 AM</span>
                           </div>
                           <p className="text-xs text-slate-500 truncate">Confirmed appointment for...</p>
                        </div>
                        <div className="p-4 hover:bg-slate-50 cursor-pointer border-l-4 border-transparent">
                           <div className="flex justify-between mb-1">
                             <span className="font-bold text-sm text-slate-900">Emily Chen</span>
                             <span className="text-[10px] text-slate-500">Yesterday</span>
                           </div>
                           <p className="text-xs text-slate-500 truncate">Thanks for the check-up!</p>
                        </div>
                      </div>
                   </div>

                   {/* Chat Area */}
                   <div className="flex-1 bg-white flex flex-col relative">
                      <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                         <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">SM</div>
                            <div>
                               <h3 className="font-bold text-sm text-slate-900">Sarah Miller</h3>
                               <div className="flex items-center gap-1.5">
                                 <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                 <span className="text-xs text-slate-500">Active Now</span>
                               </div>
                            </div>
                         </div>
                         <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="h-8"><Phone className="w-4 h-4 mr-2" /> Call</Button>
                            <Button size="sm" variant="outline" className="h-8"><Video className="w-4 h-4" /></Button>
                         </div>
                      </div>
                      
                      <div className="flex-1 p-6 bg-slate-50 space-y-4">
                         {/* Messages */}
                         <div className="flex justify-end">
                            <div className="bg-blue-600 text-white p-3 rounded-2xl rounded-tr-none text-sm max-w-[80%] shadow-sm">
                               Hi Sarah! We have an opening tomorrow at 2 PM if you'd like to come in earlier?
                            </div>
                         </div>
                         <div className="flex justify-start">
                            <div className="bg-white border border-slate-200 text-slate-700 p-3 rounded-2xl rounded-tl-none text-sm max-w-[80%] shadow-sm">
                               That would be perfect! See you then.
                            </div>
                         </div>
                         
                         {/* AI Badge Overlay */}
                         <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg border border-purple-100 flex items-center gap-2 animate-bounce">
                            <Brain className="w-4 h-4 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-900">AI Auto-Scheduled for 2:00 PM</span>
                         </div>
                      </div>
                   </div>
                </div>
              </div>
              
              {/* Floating Integrations */}
              <div className="absolute -bottom-8 -right-8 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 animate-float" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-3">
                   <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <Database className="w-5 h-5 text-indigo-600" />
                   </div>
                   <div>
                      <div className="text-sm font-bold text-slate-900">Migration Complete</div>
                      <div className="text-xs text-green-600 font-medium">Live on Asha</div>
                   </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid - Spruce-Inspired Clean Cards */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Modern Communication, <br/>
              <span className="text-blue-600">Built for Dentistry</span>
            </h2>
            <p className="text-lg text-slate-600">
              Replace your outdated phone system and fragmented messaging tools with one unified platform that thinks like a dental professional.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Card 1: Team Messaging (Collaborative) */}
            <div className="group rounded-[2.5rem] bg-slate-50 border border-slate-100 p-8 hover:shadow-xl transition-all duration-300 hover:border-blue-200">
              <div className="h-48 mb-8 relative flex items-center justify-center">
                 {/* Mock Internal Chat */}
                 <div className="bg-white p-4 rounded-2xl shadow-md border border-slate-100 w-64 transform group-hover:scale-105 transition-transform">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-50">
                       <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-[10px] font-bold text-yellow-700">JD</div>
                       <span className="text-xs font-bold text-slate-700">Internal Note</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                       <span className="text-blue-600 font-semibold">@Dr.Smith</span> Patient is asking about the whitening promo.
                    </p>
                 </div>
                 {/* Floating Avatar */}
                 <div className="absolute -right-2 top-10 w-10 h-10 bg-blue-600 rounded-full border-4 border-slate-50 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                    DS
                 </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Team Collaboration</h3>
              <p className="text-slate-600 leading-relaxed">
                Coordinate care without leaving the inbox. Use <span className="font-semibold text-blue-600">@mentions</span> and <span className="font-semibold text-blue-600">internal notes</span> to chat with your team right alongside patient messages.
              </p>
            </div>

            {/* Card 2: Unified Inbox (All Channels) */}
            <div className="group rounded-[2.5rem] bg-slate-50 border border-slate-100 p-8 hover:shadow-xl transition-all duration-300 hover:border-blue-200">
              <div className="h-48 mb-8 relative flex items-center justify-center">
                 <div className="relative w-full max-w-[240px]">
                    <div className="absolute top-0 right-0 bg-white p-3 rounded-xl shadow-lg border border-slate-100 flex items-center gap-2 z-20">
                       <Mail className="w-4 h-4 text-orange-500" />
                       <span className="text-xs font-bold text-slate-700">Email</span>
                    </div>
                    <div className="absolute bottom-0 left-0 bg-white p-3 rounded-xl shadow-lg border border-slate-100 flex items-center gap-2 z-20">
                       <Phone className="w-4 h-4 text-green-500" />
                       <span className="text-xs font-bold text-slate-700">Voice</span>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white p-4 rounded-2xl shadow-xl z-10 flex flex-col items-center">
                       <Inbox className="w-8 h-8 mb-2" />
                       <span className="text-sm font-bold">One Inbox</span>
                    </div>
                 </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Universal Inbox</h3>
              <p className="text-slate-600 leading-relaxed">
                Stop toggling between tabs. Handle SMS, email, voicemail, and web chat in a single, organized stream.
              </p>
            </div>

            {/* Card 3: Mobile App */}
            <div className="group rounded-[2.5rem] bg-slate-50 border border-slate-100 p-8 hover:shadow-xl transition-all duration-300 hover:border-blue-200">
              <div className="h-48 mb-8 relative flex items-center justify-center">
                 <div className="w-32 h-full bg-slate-900 rounded-[2rem] p-2 border-4 border-slate-200 shadow-xl transform rotate-6 group-hover:rotate-0 transition-all">
                    <div className="w-full h-full bg-white rounded-[1.5rem] overflow-hidden relative">
                       <div className="absolute top-0 w-full h-6 bg-slate-100 flex justify-center items-center">
                          <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
                       </div>
                       <div className="mt-8 px-3 space-y-2">
                          <div className="h-8 bg-blue-50 rounded-lg w-full"></div>
                          <div className="h-16 bg-slate-50 rounded-lg w-full"></div>
                          <div className="h-16 bg-slate-50 rounded-lg w-full"></div>
                       </div>
                    </div>
                 </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Practice in Your Pocket</h3>
              <p className="text-slate-600 leading-relaxed">
                Securely message patients, check your schedule, and manage calls from your personal device with our HIPAA-compliant app.
              </p>
            </div>

            {/* Large Card: AI Automation (Spruce's "Workflow Automation" but AI-native) */}
            <div className="md:col-span-3 rounded-[2.5rem] bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-10 relative overflow-hidden">
               <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
                  <div>
                     <div className="inline-block bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-semibold mb-6 border border-white/20">
                        ✨ Autonomous Workflows
                     </div>
                     <h3 className="text-3xl md:text-4xl font-bold mb-4">
                        Put Your Busywork on Autopilot
                     </h3>
                     <p className="text-blue-100 text-lg leading-relaxed mb-8 max-w-lg">
                        Unlike standard automation rules, our AI agents intelligently handle complex tasks like rescheduling, insurance verification, and post-op follow-ups without you lifting a finger.
                     </p>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-xl border border-white/10">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span className="font-medium">Missed Call Auto-Text</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-xl border border-white/10">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span className="font-medium">Insurance Collection</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-xl border border-white/10">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span className="font-medium">Recall Reactivation</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-xl border border-white/10">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span className="font-medium">Review Requests</span>
                        </div>
                     </div>
                  </div>
                  
                  {/* Graphic */}
                  <div className="relative hidden md:block">
                     <div className="absolute inset-0 bg-blue-500/30 blur-3xl rounded-full"></div>
                     <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                        <div className="flex items-start gap-4 mb-6">
                           <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center font-bold text-white shrink-0">
                              AI
                           </div>
                           <div className="bg-white/90 text-slate-800 p-4 rounded-2xl rounded-tl-none text-sm shadow-lg">
                              <p className="mb-2">I noticed you have a cleaning due next month. Would you like the 2:00 PM slot on Tuesday?</p>
                              <div className="flex gap-2">
                                 <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-bold">Smart Scheduling</span>
                              </div>
                           </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-blue-200 justify-center">
                           <Zap className="w-4 h-4" />
                           <span>Powered by DentalLLM-4</span>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

          </div>
        </div>
      </section>

      {/* Deep Dive: Unified Timeline Details (Preserved as requested) */}
      <section className="py-24 px-6 bg-slate-900 text-white overflow-hidden">
        <div className="container mx-auto max-w-7xl">
           <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium mb-6">
                   <History className="w-4 h-4" />
                   <span>The Truth Source</span>
                 </div>
                 <h2 className="text-4xl font-bold mb-6 leading-tight">
                   Everything in one place.<br />
                   <span className="text-blue-400">Nothing slips through.</span>
                 </h2>
                 <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                   When a patient calls, you shouldn't have to scramble. Our Unified Timeline gives you the complete story instantly.
                 </p>
                 
                 <div className="space-y-6">
                   <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-900/50 flex items-center justify-center text-blue-400 shrink-0 mt-1">
                         <MessageSquare className="w-5 h-5" />
                      </div>
                      <div>
                         <h4 className="font-bold text-white text-lg">Communication History</h4>
                         <p className="text-slate-400 text-sm">Every SMS, Email, and Voice Call log.</p>
                      </div>
                   </div>
                   <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-purple-900/50 flex items-center justify-center text-purple-400 shrink-0 mt-1">
                         <Brain className="w-5 h-5" />
                      </div>
                      <div>
                         <h4 className="font-bold text-white text-lg">AI Activity Logs</h4>
                         <p className="text-slate-400 text-sm">Transcripts of AI calls and automated bookings.</p>
                      </div>
                   </div>
                   <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-green-900/50 flex items-center justify-center text-green-400 shrink-0 mt-1">
                         <Shield className="w-5 h-5" />
                      </div>
                      <div>
                         <h4 className="font-bold text-white text-lg">System Events</h4>
                         <p className="text-slate-400 text-sm">Insurance verifications, forms sent, and check-ins.</p>
                      </div>
                   </div>
                 </div>
              </div>

              <div className="relative">
                 <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-2xl opacity-20"></div>
                 <div className="relative bg-slate-800 rounded-3xl border border-slate-700 p-8 shadow-2xl">
                    <div className="space-y-6">
                       {/* Mock Item 1 */}
                       <div className="flex gap-4 opacity-50">
                          <div className="flex flex-col items-center gap-1">
                             <div className="w-2 h-2 bg-slate-600 rounded-full"></div>
                             <div className="w-0.5 h-full bg-slate-700"></div>
                          </div>
                          <div className="pb-6">
                             <div className="text-xs text-slate-500 mb-1">Yesterday, 4:00 PM</div>
                             <div className="bg-slate-700/50 p-3 rounded-xl border border-slate-600">
                                <p className="text-slate-300 text-sm">Patient form "Medical History" completed.</p>
                             </div>
                          </div>
                       </div>

                       {/* Mock Item 2 */}
                       <div className="flex gap-4">
                          <div className="flex flex-col items-center gap-1">
                             <div className="w-3 h-3 bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                             <div className="w-0.5 h-full bg-slate-700"></div>
                          </div>
                          <div className="pb-6">
                             <div className="text-xs text-blue-400 mb-1">Today, 9:00 AM</div>
                             <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                   <Shield className="w-4 h-4 text-blue-400" />
                                   <span className="font-bold text-blue-200 text-sm">Insurance Verified</span>
                                </div>
                                <p className="text-slate-300 text-sm">MetLife PPO • $2,000 Annual Max • $1,500 Remaining</p>
                             </div>
                          </div>
                       </div>

                       {/* Mock Item 3 */}
                       <div className="flex gap-4">
                          <div className="flex flex-col items-center gap-1">
                             <div className="w-3 h-3 bg-green-500 rounded-full shadow-[0_0_10px_rgba(34,197,94,0.5)]"></div>
                          </div>
                          <div>
                             <div className="text-xs text-green-400 mb-1">Today, 9:15 AM</div>
                             <div className="bg-green-900/20 p-4 rounded-xl border border-green-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                   <MapPin className="w-4 h-4 text-green-400" />
                                   <span className="font-bold text-green-200 text-sm">Checked In</span>
                                </div>
                                <p className="text-slate-300 text-sm">Patient arrived at front desk.</p>
                             </div>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Modernize Your Front Desk?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join dental practices using our AI-native platform to save time, reduce missed calls, and delight patients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-7 rounded-full text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-2xl w-full sm:w-auto">
                Start Your Free Trial
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-7 rounded-full text-lg font-semibold transition-all duration-300 w-full sm:w-auto"
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TwoWayMessagingPage;
